import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ShiftStatistics from './components/ShiftStatistics';
import QuickActions from './components/QuickActions';
import IntegrationStatus from './components/IntegrationStatus';
import ShiftCard from './components/ShiftCard';
import ShiftFilters from './components/ShiftFilters';
import BulkActions from './components/BulkActions';
import ViewToggle from './components/ViewToggle';
import CalendarView from './components/CalendarView';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';


const MyAssignedShifts = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [expandedShifts, setExpandedShifts] = useState({});
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    department: 'all',
    shiftType: 'all',
    search: ''
  });

  // Mock shift data
  const [shifts] = useState([
    {
      id: 1,
      title: "Frühschicht Produktion",
      code: "PROD-001",
      date: "2025-10-04",
      startTime: "06:00",
      endTime: "14:00",
      location: "Produktionshalle A",
      payRate: 18.50,
      status: "confirmed",
      supervisor: {
        name: "Maria Schmidt",
        phone: "+49 123 456 789"
      },
      breaks: [
        { start: "09:00", end: "09:15", duration: 15 },
        { start: "12:00", end: "12:30", duration: 30 }
      ],
      requirements: ["Sicherheitsschuhe", "Schutzbrille"],
      notes: "Bitte 15 Minuten früher erscheinen für Sicherheitseinweisung.",
      teamSize: 8,
      department: "production"
    },
    {
      id: 2,
      title: "Spätschicht Lager",
      code: "WARE-002",
      date: "2025-10-05",
      startTime: "14:00",
      endTime: "22:00",
      location: "Lager B",
      payRate: 17.25,
      status: "confirmed",
      supervisor: {
        name: "Thomas Müller",
        phone: "+49 123 456 790"
      },
      breaks: [
        { start: "17:00", end: "17:15", duration: 15 },
        { start: "19:30", end: "20:00", duration: 30 }
      ],
      requirements: ["Gabelstaplerschein", "Sicherheitsweste"],
      notes: "Inventur am Ende der Schicht durchführen.",
      teamSize: 5,
      department: "warehouse"
    },
    {
      id: 3,
      title: "Wochenendschicht Service",
      code: "SERV-003",
      date: "2025-10-06",
      startTime: "08:00",
      endTime: "16:00",
      location: "Kundenservice Zentrum",
      payRate: 19.75,
      status: "pending",
      supervisor: {
        name: "Anna Weber",
        phone: "+49 123 456 791"
      },
      breaks: [
        { start: "10:30", end: "10:45", duration: 15 },
        { start: "13:00", end: "13:30", duration: 30 }
      ],
      requirements: ["Kundenservice Zertifikat"],
      notes: "Erhöhtes Anrufaufkommen erwartet.",
      teamSize: 12,
      department: "customer-service"
    },
    {
      id: 4,
      title: "Nachtschicht Wartung",
      code: "MAINT-004",
      date: "2025-10-07",
      startTime: "22:00",
      endTime: "06:00",
      location: "Wartungswerkstatt",
      payRate: 22.00,
      status: "confirmed",
      supervisor: {
        name: "Klaus Fischer",
        phone: "+49 123 456 792"
      },
      breaks: [
        { start: "01:00", end: "01:15", duration: 15 },
        { start: "03:30", end: "04:00", duration: 30 }
      ],
      requirements: ["Elektriker Zertifikat", "Sicherheitsausrüstung"],
      notes: "Planmäßige Wartung der Maschinen 1-5.",
      teamSize: 3,
      department: "maintenance"
    },
    {
      id: 5,
      title: "Frühschicht Qualitätskontrolle",
      code: "QC-005",
      date: "2025-10-08",
      startTime: "07:00",
      endTime: "15:00",
      location: "QC Labor",
      payRate: 20.25,
      status: "confirmed",
      supervisor: {
        name: "Petra Hoffmann",
        phone: "+49 123 456 793"
      },
      breaks: [
        { start: "10:00", end: "10:15", duration: 15 },
        { start: "12:30", end: "13:00", duration: 30 }
      ],
      requirements: ["QC Zertifikat", "Laborkittel"],
      notes: "Neue Produktcharge zu prüfen.",
      teamSize: 4,
      department: "production"
    }
  ]);

  // Filter shifts based on current filters
  const filteredShifts = shifts?.filter(shift => {
    if (filters?.status !== 'all' && shift?.status !== filters?.status) return false;
    if (filters?.department !== 'all' && shift?.department !== filters?.department) return false;
    if (filters?.startDate && shift?.date < filters?.startDate) return false;
    if (filters?.endDate && shift?.date > filters?.endDate) return false;
    if (filters?.search && !shift?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) && 
        !shift?.code?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    return true;
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      status: 'all',
      department: 'all',
      shiftType: 'all',
      search: ''
    });
  };

  const handleShiftSelect = (shiftId) => {
    setSelectedShifts(prev => 
      prev?.includes(shiftId) 
        ? prev?.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const handleSelectAll = () => {
    setSelectedShifts(filteredShifts?.map(shift => shift?.id));
  };

  const handleDeselectAll = () => {
    setSelectedShifts([]);
  };

  const handleBulkAction = (action, shiftIds) => {
    console.log(`Bulk action: ${action}`, shiftIds);
    // Implement bulk actions here
    setSelectedShifts([]);
  };

  const handleToggleExpand = (shiftId) => {
    setExpandedShifts(prev => ({
      ...prev,
      [shiftId]: !prev?.[shiftId]
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-80 bg-card border-r border-border p-6 space-y-6 fixed left-0 top-16 bottom-0 overflow-y-auto">
            <ShiftStatistics />
            <QuickActions />
            <IntegrationStatus />
          </div>

          {/* Main Content */}
          <div className="flex-1 ml-80 p-6">
            <Breadcrumb />
            
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Meine zugewiesenen Schichten</h1>
                  <p className="text-muted-foreground mt-1">
                    Verwalten Sie Ihre bestätigten Schichtzuweisungen und verfolgen Sie Ihren Zeitplan
                  </p>
                </div>
                <ViewToggle currentView={currentView} onViewChange={setCurrentView} />
              </div>
            </div>

            <ShiftFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />

            {currentView === 'list' && (
              <>
                <BulkActions
                  selectedShifts={selectedShifts}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  onBulkAction={handleBulkAction}
                />

                <div className="space-y-4">
                  {filteredShifts?.length > 0 ? (
                    filteredShifts?.map((shift) => (
                      <div key={shift?.id} className="flex items-start space-x-4">
                        <div className="pt-4">
                          <Checkbox
                            checked={selectedShifts?.includes(shift?.id)}
                            onChange={() => handleShiftSelect(shift?.id)}
                          />
                        </div>
                        <div className="flex-1">
                          <ShiftCard
                            shift={shift}
                            isExpanded={expandedShifts?.[shift?.id] || false}
                            onToggle={() => handleToggleExpand(shift?.id)}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="Calendar" size={32} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Keine Schichten gefunden
                      </h3>
                      <p className="text-muted-foreground">
                        Keine Schichten entsprechen den aktuellen Filterkriterien.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {currentView === 'calendar' && (
              <CalendarView
                shifts={filteredShifts}
                selectedShifts={selectedShifts}
                onShiftSelect={handleShiftSelect}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAssignedShifts;